/**
 * Stickers as the web client holds them (docs/stickers-plan.md D546–D566):
 * pure folds over the `stickers` slice, applied inside the runtime's
 * `mutate`. The desktop's `state/stickers.rs`, fold for fold — the same
 * catalogue generations, the same installed-first order, the same recents,
 * the same answers to one emoji in the box (D574).
 */
import type {
  Sticker,
  StickerCatalog,
  StickerPack,
  StickerPackList,
  StickerPackSummary,
} from "../gen/dto";
import { MAX_SUGGESTIONS, PREVIEW_STICKERS } from "../lib/stickers";
import type { StickerCatalogState, StickerSheet, StickersState } from "./types";

/** How many recent stickers the panel keeps (D561) — the gateway's ceiling. */
export const MAX_RECENTS = 20;

/** A page of the catalogue to ask for. */
export interface CatalogAsk {
  generation: number;
  query: string;
  order: "popular" | "new";
  cursor: string | null;
}

/** A pack's row out of the pack read whole, with its first `preview`. */
export function summaryOf(pack: StickerPack, preview: number): StickerPackSummary {
  return {
    id: pack.id,
    owner_id: pack.owner_id,
    title: pack.title,
    status: pack.status,
    installs: pack.installs,
    count: pack.stickers.length,
    installed: pack.installed,
    preview: pack.stickers.slice(0, preview),
  };
}

// ---------------------------------------------------------------------------
// The catalogue (D566)

/** A question from the search or the order switch: the list starts over. The
 * question already on screen asks nothing, unless its first page never came. */
export function askCatalog(
  c: StickerCatalogState,
  query: string,
  order: "popular" | "new",
): CatalogAsk | null {
  const q = query.trim();
  const same = c.asked && c.query === q && c.order === order;
  if (same && !(c.failed && c.packs.length === 0)) return null;
  c.asked = true;
  c.generation += 1;
  c.query = q;
  c.order = order;
  c.packs = [];
  c.cursor = null;
  c.done = false;
  c.failed = false;
  c.loading = true;
  return { generation: c.generation, query: q, order, cursor: null };
}

/** The next page — null while one is on its way, past the last, before
 * anything was asked, and after a page failed: the list's end is in reach on
 * every scroll event, and a word search spends the gateway's message-search
 * budget (D399) on each ask, so a failure is asked again only at the press on
 * its row (`retryCatalog`). */
export function moreCatalog(c: StickerCatalogState): CatalogAsk | null {
  if (!c.asked || c.loading || c.done || c.failed) return null;
  c.loading = true;
  return { generation: c.generation, query: c.query, order: c.order, cursor: c.cursor };
}

/** The press on «Could not load»: the page that failed, asked again once — the
 * question's first page when nothing is listed, the next one after what is. */
export function retryCatalog(c: StickerCatalogState): CatalogAsk | null {
  if (!c.failed) return null;
  if (c.packs.length === 0) return askCatalog(c, c.query, c.order);
  c.failed = false;
  return moreCatalog(c);
}

/** A page landed; false when it belonged to an older question. A pack already
 * listed is dropped — a keyset over installs moves under the reading. */
export function landCatalog(
  c: StickerCatalogState,
  generation: number,
  page: StickerCatalog,
): boolean {
  if (generation !== c.generation) return false;
  c.loading = false;
  c.failed = false;
  const seen = new Set(c.packs.map((p) => p.id));
  for (const pack of page.packs) {
    if (!seen.has(pack.id)) {
      seen.add(pack.id);
      c.packs.push(pack);
    }
  }
  c.cursor = page.cursor ?? null;
  c.done = page.cursor == null;
  return true;
}

export function failCatalog(c: StickerCatalogState, generation: number): boolean {
  if (generation !== c.generation) return false;
  c.loading = false;
  c.failed = true;
  return true;
}

// ---------------------------------------------------------------------------
// Packs, installs, recents

/** How many writes `packId` has had: what a read of it asked now carries. */
export function packEpoch(s: StickersState, packId: number): number {
  return s.packWrites[packId] ?? 0;
}

/** A write to `packId` was answered: a read asked before it no longer lands. */
function wrote(s: StickersState, packId: number): void {
  s.packWrites[packId] = packEpoch(s, packId) + 1;
}

/** The installed list landed — the gateway's order, and stickers exist here —
 * unless it was asked before the last install, removal or pack forgotten
 * (`epoch`, the list's writes at the ask): the gateway may have answered it
 * before that write, so it lands nothing and is asked again. */
export function landInstalled(
  s: StickersState,
  list: StickerPackList,
  epoch: number = s.installedWrites,
): boolean {
  s.support = "yes";
  if (epoch !== s.installedWrites) return false;
  s.installed = list.packs.filter((p) => p.status === "published");
  const installed = new Set(s.installed.map((p) => p.id));
  for (const pack of Object.values(s.packs)) pack.installed = installed.has(pack.id);
  for (const row of [...s.catalog.packs, ...s.own]) row.installed = installed.has(row.id);
  return true;
}

export function landRecents(s: StickersState, recents: Sticker[]): void {
  s.recents = recents.slice(0, MAX_RECENTS);
}

/** Claims a pack for a read: false while one is on its way, or — unless
 * `again` — once it is read. A claim lets the last failure go. */
export function claimPack(s: StickersState, packId: number, again: boolean): boolean {
  if (s.loading.includes(packId) || (!again && s.packs[packId] !== undefined)) return false;
  s.loading.push(packId);
  s.failed = s.failed.filter((x) => x !== packId);
  return true;
}

/** A pack's read answered: whole, as `landPack` lands it — unless the pack
 * was written to since the read was claimed at `epoch`. The gateway may have
 * answered the read before that write, so landing it could put back what the
 * write changed — an install undone, a sticker just added gone, a deleted
 * pack back — and it lands nothing: false, and the read is asked again. */
export function landRead(s: StickersState, pack: StickerPack, epoch: number): boolean {
  if (epoch !== packEpoch(s, pack.id)) {
    s.loading = s.loading.filter((x) => x !== pack.id);
    return false;
  }
  landPack(s, pack);
  return true;
}

/** A pack read whole — and what every write to one answers. Its row moves in
 * every list, and the installed list follows what it says. */
export function landPack(s: StickersState, pack: StickerPack): void {
  const id = pack.id;
  s.loading = s.loading.filter((x) => x !== id);
  s.missing = s.missing.filter((x) => x !== id);
  s.failed = s.failed.filter((x) => x !== id);
  const at = s.installed.findIndex((p) => p.id === id);
  const sendable = pack.status === "published" && pack.installed;
  if (at >= 0 && sendable) s.installed[at] = summaryOf(pack, 1);
  else if (at >= 0) s.installed.splice(at, 1);
  else if (sendable) s.installed.unshift(summaryOf(pack, 1));
  const inCatalog = s.catalog.packs.findIndex((p) => p.id === id);
  if (inCatalog >= 0) s.catalog.packs[inCatalog] = summaryOf(pack, PREVIEW_STICKERS);
  const inOwn = s.own.findIndex((p) => p.id === id);
  if (inOwn >= 0) s.own[inOwn] = summaryOf(pack, PREVIEW_STICKERS);
  s.packs[id] = pack;
}

/** Out of every list: deleted, taken down, or gone for this reader. What was
 * sent from it stays the feed's (D551). A read still out for it lands
 * nothing: it would bring the pack back. */
export function forgetPack(s: StickersState, packId: number): void {
  wrote(s, packId);
  s.installedWrites += 1;
  s.installed = s.installed.filter((p) => p.id !== packId);
  s.recents = s.recents.filter((x) => x.pack_id !== packId);
  for (const [emoji, offered] of Object.entries(s.suggestions)) {
    s.suggestions[emoji] = offered.filter((x) => x.pack_id !== packId);
  }
  delete s.packs[packId];
  s.catalog.packs = s.catalog.packs.filter((p) => p.id !== packId);
  s.own = s.own.filter((p) => p.id !== packId);
}

/** A 404: out of every list, and a sheet about it says so. */
export function packGone(s: StickersState, packId: number): void {
  s.loading = s.loading.filter((x) => x !== packId);
  s.failed = s.failed.filter((x) => x !== packId);
  if (!s.missing.includes(packId)) s.missing.push(packId);
  forgetPack(s, packId);
}

/** A read that failed for anything but a 404: a sheet about the pack says so,
 * and asks again at a press (`claimPack` with `again`). */
export function packFailed(s: StickersState, packId: number): void {
  s.loading = s.loading.filter((x) => x !== packId);
  if (!s.failed.includes(packId)) s.failed.push(packId);
}

/** Installed, or taken out, as the gateway answered (D560): a new one first,
 * where the gateway puts it. */
export function setInstalled(s: StickersState, packId: number, on: boolean): void {
  wrote(s, packId);
  s.installedWrites += 1;
  const was = s.installed.some((p) => p.id === packId);
  if (on && !was) {
    const whole = s.packs[packId];
    const row =
      whole !== undefined
        ? summaryOf(whole, PREVIEW_STICKERS)
        : [...s.catalog.packs, ...s.own].find((p) => p.id === packId);
    if (row !== undefined) {
      s.installed.unshift({ ...row, installed: true, preview: row.preview.slice(0, 1) });
    }
  } else if (!on) {
    s.installed = s.installed.filter((p) => p.id !== packId);
  }
  const step = (installs: number) =>
    on && !was ? installs + 1 : !on && was ? Math.max(0, installs - 1) : installs;
  const whole = s.packs[packId];
  if (whole !== undefined) {
    whole.installed = on;
    whole.installs = step(whole.installs);
  }
  for (const row of [...s.catalog.packs, ...s.own]) {
    if (row.id !== packId) continue;
    row.installed = on;
    row.installs = step(row.installs);
  }
}

/** A sticker just sent: first in the recents (D561). */
export function noteSent(s: StickersState, sticker: Sticker): void {
  s.recents = [sticker, ...s.recents.filter((x) => x.file_id !== sticker.file_id)].slice(
    0,
    MAX_RECENTS,
  );
}

export function landOwn(s: StickersState, list: StickerPackList): void {
  s.own = list.packs;
}

/** A pack the reader just made: first among their own, and read whole — and
 * its editor opens, if the reader's packs are still the sheet on screen (D566).
 * A sheet closed or left while the request was out stays as the reader left
 * it. */
export function landCreated(s: StickersState, pack: StickerPack): void {
  s.own = [summaryOf(pack, PREVIEW_STICKERS), ...s.own.filter((p) => p.id !== pack.id)];
  wrote(s, pack.id);
  landPack(s, pack);
  if (s.sheet?.t === "editor" && s.sheet.id === null) openSheet(s, { t: "editor", id: pack.id });
}

/** What the editor's rename and publish answer: the pack whole, as written. */
export function landEdit(s: StickersState, pack: StickerPack): void {
  wrote(s, pack.id);
  landPack(s, pack);
}

/** A sticker added to the reader's pack, or changed in it. */
export function landSticker(s: StickersState, sticker: Sticker): void {
  wrote(s, sticker.pack_id);
  const pack = s.packs[sticker.pack_id];
  if (pack === undefined) return;
  const at = pack.stickers.findIndex((x) => x.file_id === sticker.file_id);
  if (at >= 0) pack.stickers[at] = sticker;
  else pack.stickers.push(sticker);
  landPack(s, { ...pack, stickers: [...pack.stickers] });
}

/** A sticker taken out of the reader's pack. */
export function dropSticker(s: StickersState, packId: number, fileId: string): void {
  wrote(s, packId);
  s.recents = s.recents.filter((x) => !(x.pack_id === packId && x.file_id === fileId));
  for (const [emoji, offered] of Object.entries(s.suggestions)) {
    s.suggestions[emoji] = offered.filter((x) => !(x.pack_id === packId && x.file_id === fileId));
  }
  const pack = s.packs[packId];
  if (pack === undefined) return;
  landPack(s, { ...pack, stickers: pack.stickers.filter((x) => x.file_id !== fileId) });
}

// ---------------------------------------------------------------------------
// One emoji's stickers (D574)

/** Claims the question «what does this emoji offer»: false while it is on its
 * way, and on a gateway without stickers. A question answered before is asked
 * again — the recents and the installed packs move — and its old answer stays
 * on screen until the new one lands. */
export function claimSuggestions(s: StickersState, emoji: string): boolean {
  if (s.support === "no" || s.suggesting.includes(emoji)) return false;
  s.suggesting.push(emoji);
  return true;
}

/** The answer for `emoji`, ten at most. */
export function landSuggestions(s: StickersState, emoji: string, stickers: Sticker[]): void {
  s.suggesting = s.suggesting.filter((x) => x !== emoji);
  s.suggestions[emoji] = stickers.slice(0, MAX_SUGGESTIONS);
}

/** The question failed — a gateway older than the route among the reasons:
 * what was answered before stays, and the next time asks again. */
export function suggestionsFailed(s: StickersState, emoji: string): void {
  s.suggesting = s.suggesting.filter((x) => x !== emoji);
}

// ---------------------------------------------------------------------------
// The sheet

function same(a: StickerSheet | null, b: StickerSheet | null): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/** Puts a sheet up, or takes it down (null), remembering where its arrow goes
 * back to; leaving the editor lets its last refusal go. */
export function openSheet(s: StickersState, sheet: StickerSheet | null): void {
  const from = s.sheet;
  if (from?.t === "editor" && sheet?.t !== "editor") s.editor = { busy: false, error: null };
  if (same(from, sheet)) {
    // Read again: the arrow stays where it was.
  } else if (
    from?.t === "market" &&
    (sheet?.t === "pack" || (sheet?.t === "editor" && sheet.id === null))
  ) {
    s.back = { t: "market" };
  } else if (
    from?.t === "editor" &&
    from.id === null &&
    sheet?.t === "editor" &&
    sheet.id !== null
  ) {
    s.back = { t: "editor", id: null };
  } else {
    s.back = null;
  }
  s.sheet = sheet;
}
